import React, {useEffect, useState} from 'react';
import {Box, Button, Grid, IconButton, Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {Outlet} from 'react-router-dom';
import {useSnackbar} from 'notistack';
import {useObservable} from '@ngneat/react-rxjs';
import {finalize} from 'rxjs';

import {Add, Delete, GroupAction} from '@icons/index';

import InfosCard from '@components/cards/infos/Infos.card';
import AlertComponent from '@components/modal/Alert.component';

import {Colors} from '@constants/colors.constants';
import {IssueAudienceKindEnum, IssueKind} from '@store/settings/issueKinds/issueKinds.model';
import {issueKindsService} from '@store/settings/issueKinds/issueKinds.services';
import ModalComponent from '@components/modal/Modal.component';
import TextFieldComponent from '@components/inputs/textField/TextField.component';
import SelectComponent from '@components/inputs/select/Select.component';
import {issueKindsQuery} from '@store/settings/issueKinds/issueKinds.query';
import {IssueKindType} from '@store/operations/issues';

const IssueKindsTab = () => {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDelete, setShowDelete] = useState<number | false>(false);
  const [showUpdate, setShowUpdate] = useState<boolean>(false);
  const [{issueKinds}] = useObservable(issueKindsQuery.issueKinds$);
  const [isUpdate, setIsUpdate] = useState<boolean>(false);
  const [createIssueKind, setCreateIssueKind] = useState<Partial<IssueKind>>();
  const [issueKindId, setIssueKindId] = useState<number>();

  const updateCreateIssueKind = (updatedIssueKind: Partial<IssueKind>) => {
    setCreateIssueKind((prevIssueKind) => ({
      ...prevIssueKind,
      ...updatedIssueKind,
    }));
  };

  useEffect(() => {
    issueKindsService.getIssueKinds().subscribe({
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  }, [enqueueSnackbar]);

  const handleSave = () => {
    if (!isUpdate) {
      if (createIssueKind) {
        issueKindsService.createIssueKind(createIssueKind).pipe()
          .subscribe({
            next: () => {
              enqueueSnackbar(t('settings.issueKind.success.add'), {variant: 'success'});
              setShowUpdate(false);
              setCreateIssueKind(undefined);
            },
            error: (err) => enqueueSnackbar(err.text, err.options),
          });
      }

    } else {
      if (createIssueKind && issueKindId) {
        issueKindsService.updateIssueKind(issueKindId, createIssueKind).pipe()
          .subscribe({
            next: () => {
              enqueueSnackbar(t('settings.issueKinds.success.edit'), {variant: 'success'});
              setShowUpdate(false);
              setCreateIssueKind(undefined);
            },
            error: (err) => enqueueSnackbar(err.text, err.options),
          });
      }

    }
  };

  return (
    <>
      <Stack flex={1} spacing={1} p="25px" overflow="hidden">
        <Stack direction="row" justifyContent="space-between">
          <Typography fontSize="30px" fontWeight="700">
            {t('routes.issue-kinds')}
          </Typography>
          <Box alignSelf="flex-end">
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add/>}
              onClick={() => setShowUpdate(true)}>
              {t('settings.issueKinds.addButton')}
            </Button>
          </Box>
        </Stack>

        <Box flex={1} pt="25px" overflow="hidden">
          <Stack height="100%" overflow="hidden" borderRadius="4px" bgcolor={Colors.white}>
            <Stack p="30px" overflow="auto" className="scrollable">
              <Grid container spacing={2} columns={6}>
                {issueKinds?.map((issueKind) => (
                  <Grid item key={issueKind.id} xs={2}>
                    <InfosCard
                      title={issueKind.name}
                      menuOptions={(
                        <>
                          <IconButton
                            sx={{mr: '-7px'}}
                            onClick={(evt) => {
                              evt.stopPropagation();
                              setShowUpdate(true);
                              setIsUpdate(true);
                              setCreateIssueKind({
                                name: issueKind.name,
                                translationKey: issueKind.translationKey,
                                audienceKind: issueKind.audienceKind,
                                type: issueKind.type,
                              });
                              setIssueKindId(issueKind.id);
                            }}>
                            <GroupAction style={{height: 14, width: 14}}/>
                          </IconButton>
                          <IconButton
                            onClick={(evt) => {
                              evt.stopPropagation();
                              setShowDelete(issueKind.id);
                            }}>
                            <Delete style={{height: 14, width: 14}}/>
                          </IconButton>
                        </>
                      )}
                      unit={issueKind.audienceKind}
                    />
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Stack>
        </Box>
      </Stack>
      <Outlet/>
      {showDelete && (
        <AlertComponent
          title={t('delete.title')}
          description={t('delete.description.serviceKind')}
          handleClose={() => setShowDelete(false)}
          actions={(
            <>
              <Button
                variant="outlined"
                color="primary"
                disabled={deleteLoading}
                onClick={() => setShowDelete(false)}>
                {t('global.cancel')}
              </Button>
              <Button
                variant="contained"
                disabled={deleteLoading}
                color="primary"
                onClick={() => {
                  setDeleteLoading(true);
                  issueKindsService.deleteIssueKind(showDelete).pipe(finalize(() => setDeleteLoading(false))).subscribe({
                    next: () => {
                      enqueueSnackbar(t('delete.success.issueKind'), {variant: 'success'});
                      setShowDelete(false);
                    },
                    error: (err) => enqueueSnackbar(err.text, err.options),
                  });
                }}>
                {t('global.delete')}
              </Button>
            </>
          )}/>
      )}

      {showUpdate && (
        <ModalComponent
          title={t(`settings.issueKinds.manage.title.${isUpdate ? 'edit' : 'add'}`)}
          handleClose={() => setShowUpdate(false)}
          content={(
            <Stack width="70%" spacing={2} pt="55px" pb="10px">
              <TextFieldComponent
                color="secondary"
                labelWidth={160}
                label={t('settings.issueKinds.name')}
                placeholder={t('global.writeSomething')}
                handleChange={(value) => updateCreateIssueKind({name: value})}
                value={createIssueKind?.name}
              />
              <TextFieldComponent
                color="secondary"
                labelWidth={160}
                label={t('settings.issueKinds.translateKey')}
                placeholder={t('global.writeSomething')}
                handleChange={(value) => updateCreateIssueKind({translationKey: value})}
                value={createIssueKind?.translationKey}
              />
              <SelectComponent
                color="secondary"
                labelWidth={160}
                label={t('settings.issueKinds.audienceKind')}
                placeholder={t('global.selectOption')}
                handleChange={(value) => updateCreateIssueKind({audienceKind: value})}
                value={createIssueKind?.audienceKind}
                items={IssueAudienceKindEnum.selectItems()}
              />
              <SelectComponent
                color="secondary"
                labelWidth={160}
                label={t('settings.issueKinds.type')}
                placeholder={t('global.selectOption')}
                handleChange={(value) => updateCreateIssueKind({type: value})}
                value={createIssueKind?.type}
                items={IssueKindType.selectItems()}
              />
            </Stack>
          )}
          actions={(
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                onClick={handleSave}
                color="primary">
                {t('global.save')}
              </Button>
            </Stack>
          )}/>
      )}
    </>
  );
};

export default IssueKindsTab;
